import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import Layout from '../components/layout/Layout';
import MergedPlanner from '../components/tasks/MergedPlanner';
import AnalyticsDashboard from '../components/analytics/AnalyticsDashboard';
import CalendarView from '../components/calendar/CalendarView';
import BucketList from '../components/tasks/BucketList';
import AIAssistant from '../components/ai/AIAssistant';
import QuickCapture from '../components/capture/QuickCapture';
import HabitTracker from '../components/habits/HabitTracker';
import DailyReflection from '../components/reflection/DailyReflection';
import MealPlanner from '../components/meals/MealPlanner';
import CleaningChecklist from '../components/cleaning/CleaningChecklist';
import FamilyGoals from '../components/goals/FamilyGoals';
import FocusTime from '../components/focus/FocusTime';
import TimeTracking from '../components/tracking/TimeTracking';
import ShoppingList from '../components/shopping/ShoppingList';
import FamilyHub from '../components/family/FamilyHub';
import BudgetGoals from '../components/budget/BudgetGoals';
import WeatherPlanning from '../components/weather/WeatherPlanning';
import Gamification from '../components/gamification/Gamification';
import FamilyDashboard from '../components/familydashboard/FamilyDashboard';
import HealthOverlay from '../components/health/HealthOverlay';
import WeightTracker from '../components/health/WeightTracker';
import TravelPlanner from '../components/travel/TravelPlanner';
import CalendarSync from '../components/calendar/CalendarSync';
import CollaborativeWorkspaces from '../components/workspaces/CollaborativeWorkspaces';
import LocationReminders from '../components/location/LocationReminders';
import PremiumGate from '../components/subscription/PremiumGate';
import UpgradePage from '../components/subscription/UpgradePage';
import { useSubscription } from '../contexts/SubscriptionContext';
import { apiService } from '../services/api';

type ViewType = 'planner' | 'analytics' | 'calendar' | 'habits' | 'reflection' | 'meals' | 'cleaning' | 'goals' | 'focus' | 'timetracking' | 'shopping' | 'family' | 'budget' | 'weather' | 'achievements' | 'familydashboard' | 'health' | 'weighttracker' | 'travel' | 'calendarsync' | 'workspaces' | 'locationreminders' | 'upgrade';

const Dashboard: React.FC = () => {
  const { user } = useAuth();
  const { isPremium, refresh: refreshSubscription } = useSubscription();
  const [currentView, setCurrentView] = useState<ViewType>('planner');
  const [welcomeMessage, setWelcomeMessage] = useState('Your daily planner is ready. Let\'s make today productive!');
  const [showBucketList, setShowBucketList] = useState(false);
  const [showAI, setShowAI] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [refreshKey, setRefreshKey] = useState(0);

  // Fetch welcome message from settings
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await apiService.getPublicSettings();
        if (response.settings?.welcome_message) {
          setWelcomeMessage(response.settings.welcome_message);
        }
      } catch (err) {
        // Use default message on error
      }
    };
    fetchSettings();
  }, []);

  // Handle Stripe checkout return (?payment=success&session_id=...)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const payment = params.get('payment');
    const sessionId = params.get('session_id');
    if (payment === 'success' && sessionId) {
      let tries = 0;
      const poll = async () => {
        tries++;
        try {
          const res = await apiService.getStripeSessionStatus(sessionId);
          if (res.success && res.paid) {
            await refreshSubscription();
            alert('🎉 Welcome to Yojana Premium! All premium features are now unlocked.');
            window.history.replaceState({}, '', '/');
            setCurrentView('planner');
            return;
          }
        } catch (e) { /* keep polling */ }
        if (tries < 8) setTimeout(poll, 1500);
        else window.history.replaceState({}, '', '/');
      };
      poll();
    } else if (payment === 'cancel') {
      window.history.replaceState({}, '', '/');
      setCurrentView('upgrade');
    }
  }, [refreshSubscription]);

  // Fixed navigation handler with proper typing
  const handleNavigate = (page: string) => {
    const validViews: ViewType[] = ['planner', 'analytics', 'calendar', 'habits', 'reflection', 'meals', 'cleaning', 'goals', 'focus', 'timetracking', 'shopping', 'family', 'budget', 'weather', 'achievements', 'familydashboard', 'health', 'weighttracker', 'travel', 'calendarsync', 'workspaces', 'locationreminders', 'upgrade'];
    if (validViews.includes(page as ViewType)) {
      setCurrentView(page as ViewType);
    }
  };

  const handleCalendarDateSelect = (date: string) => {
    setSelectedDate(date);
    setCurrentView('planner');
  };

  return (
    <Layout currentPage={currentView} onNavigate={handleNavigate}>
      {/* Bucket List Modal */}
      <BucketList isOpen={showBucketList} onClose={() => setShowBucketList(false)} />

      {currentView === 'planner' ? (
        <div className="dashboard">
          {/* Welcome Message for Planner */}
          <div className="welcome-card" style={{ position: 'relative' }}>
            <h1 style={{ fontSize: '2.5rem', marginBottom: '1rem' }}>
              Welcome back, {user?.firstName || user?.username || 'there'}!
            </h1>
            <p style={{ fontSize: '1.2rem', opacity: '0.9' }}>
              {welcomeMessage}
            </p>
            
            {/* Bucket List Button */}
            <button
              onClick={() => setShowBucketList(true)}
              data-testid="bucket-list-btn"
              style={{
                position: 'absolute',
                top: '1rem',
                right: '1rem',
                padding: '10px 16px',
                background: 'rgba(255,255,255,0.2)',
                border: '2px solid rgba(255,255,255,0.4)',
                borderRadius: '12px',
                color: 'white',
                fontWeight: 600,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(255,255,255,0.3)';
                e.currentTarget.style.transform = 'scale(1.05)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'rgba(255,255,255,0.2)';
                e.currentTarget.style.transform = 'scale(1)';
              }}
            >
              Bucket List
            </button>

            {/* AI Assistant Toggle Button */}
            <button
              onClick={() => { if (isPremium) { setShowAI(!showAI); } else { setCurrentView('upgrade'); } }}
              data-testid="ai-assistant-btn"
              style={{
                position: 'absolute',
                top: '1rem',
                right: '140px',
                padding: '10px 16px',
                background: showAI ? 'rgba(255,255,255,0.4)' : 'rgba(255,255,255,0.2)',
                border: '2px solid rgba(255,255,255,0.4)',
                borderRadius: '12px',
                color: 'white',
                fontWeight: 600,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(255,255,255,0.3)';
                e.currentTarget.style.transform = 'scale(1.05)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = showAI ? 'rgba(255,255,255,0.4)' : 'rgba(255,255,255,0.2)';
                e.currentTarget.style.transform = 'scale(1)';
              }}
            >
              AI
            </button>
          </div>

          {/* AI Assistant Panel */}
          {showAI && isPremium && (
            <AIAssistant 
              selectedDate={selectedDate} 
              onTasksUpdated={() => setRefreshKey(k => k + 1)}
            />
          )}

          {/* Main Daily Planner - Merged View */}
          <MergedPlanner key={`${selectedDate}-${refreshKey}`} selectedDate={selectedDate} />

          {/* Quick Tips */}
          <div className="bg-white" style={{ borderRadius: '15px', padding: '2rem', marginTop: '2rem', textAlign: 'center', boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)' }}>
            <h3 style={{ marginBottom: '1rem', color: '#374151' }}>Pro Tips</h3>
            <div className="grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1rem', textAlign: 'left' }}>
              <div style={{ padding: '1rem', backgroundColor: '#f8fafc', borderRadius: '8px' }}>
                <strong>Drag to Schedule:</strong> Click and drag across time slots to quickly create a task for that duration.
              </div>
              <div style={{ padding: '1rem', backgroundColor: '#f8fafc', borderRadius: '8px' }}>
                <strong>Move Tasks:</strong> Drag unscheduled tasks onto the timeline, or click &quot;Move&quot; to reschedule to any future date.
              </div>
              <div style={{ padding: '1rem', backgroundColor: '#f8fafc', borderRadius: '8px' }}>
                <strong>Bucket List:</strong> Click the Bucket List button to capture ideas anytime, then schedule them when ready!
              </div>
            </div>
          </div>
        </div>
      ) : currentView === 'calendar' ? (
        <CalendarView onDateSelect={handleCalendarDateSelect} />
      ) : currentView === 'habits' ? (
        <div className="bg-white" style={{ borderRadius: '15px', margin: '1rem', boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)' }}>
          <HabitTracker date={selectedDate} />
        </div>
      ) : currentView === 'reflection' ? (
        <div style={{ margin: '1rem' }}>
          <DailyReflection date={selectedDate} />
        </div>
      ) : currentView === 'meals' ? (
        <div className="bg-white" style={{ borderRadius: '15px', margin: '1rem', boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)' }}>
          <MealPlanner />
        </div>
      ) : currentView === 'cleaning' ? (
        <div className="bg-white" style={{ borderRadius: '15px', margin: '1rem', boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)' }}>
          <CleaningChecklist />
        </div>
      ) : currentView === 'goals' ? (
        <div className="bg-white" style={{ borderRadius: '15px', margin: '1rem', boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)' }}>
          <FamilyGoals />
        </div>
      ) : currentView === 'focus' ? (
        <div className="bg-white" style={{ borderRadius: '15px', margin: '1rem', boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)' }}>
          <FocusTime selectedDate={selectedDate} />
        </div>
      ) : currentView === 'timetracking' ? (
        <div className="bg-white" style={{ borderRadius: '15px', margin: '1rem', boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)' }}>
          <TimeTracking />
        </div>
      ) : currentView === 'shopping' ? (
        <div className="bg-white" style={{ borderRadius: '15px', margin: '1rem', boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)' }}>
          <ShoppingList />
        </div>
      ) : currentView === 'family' || currentView === 'familydashboard' ? (
        <div className="bg-white" style={{ borderRadius: '15px', margin: '1rem', boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)' }}>
          <PremiumGate feature="Family Sharing" description="Share and coordinate tasks with your family." onUpgrade={() => setCurrentView('upgrade')}>
            <FamilyHub />
          </PremiumGate>
        </div>
      ) : currentView === 'budget' ? (
        <div className="bg-white" style={{ borderRadius: '15px', margin: '1rem', boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)' }}>
          <BudgetGoals />
        </div>
      ) : currentView === 'weather' ? (
        <div className="bg-white" style={{ borderRadius: '15px', margin: '1rem', boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)' }}>
          <WeatherPlanning selectedDate={selectedDate} />
        </div>
      ) : currentView === 'achievements' ? (
        <div className="bg-white" style={{ borderRadius: '15px', margin: '1rem', boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)' }}>
          <Gamification />
        </div>
      ) : currentView === 'health' ? (
        <div className="bg-white" style={{ borderRadius: '15px', margin: '1rem', boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)' }}>
          <HealthOverlay />
        </div>
      ) : currentView === 'weighttracker' ? (
        <div className="bg-white" style={{ borderRadius: '15px', margin: '1rem', boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)' }}>
          <PremiumGate feature="Weight Tracker" onUpgrade={() => setCurrentView('upgrade')}>
            <WeightTracker />
          </PremiumGate>
        </div>
      ) : currentView === 'travel' ? (
        <div className="bg-white" style={{ borderRadius: '15px', margin: '1rem', boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)' }}>
          <PremiumGate feature="Travel Planner" onUpgrade={() => setCurrentView('upgrade')}>
            <TravelPlanner />
          </PremiumGate>
        </div>
      ) : currentView === 'calendarsync' ? (
        <div className="bg-white" style={{ borderRadius: '15px', margin: '1rem', boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)' }}>
          <CalendarSync />
        </div>
      ) : currentView === 'workspaces' ? (
        <div className="bg-white" style={{ borderRadius: '15px', margin: '1rem', boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)' }}>
          <PremiumGate feature="Workspaces" description="Share tasks and plans with your family or team." onUpgrade={() => setCurrentView('upgrade')}>
            <CollaborativeWorkspaces />
          </PremiumGate>
        </div>
      ) : currentView === 'locationreminders' ? (
        <div className="bg-white" style={{ borderRadius: '15px', margin: '1rem', boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)' }}>
          <PremiumGate feature="Location Reminders" onUpgrade={() => setCurrentView('upgrade')}>
            <LocationReminders />
          </PremiumGate>
        </div>
      ) : currentView === 'upgrade' ? (
        <div className="bg-white" style={{ borderRadius: '15px', margin: '1rem', boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)' }}>
          <UpgradePage />
        </div>
      ) : (
        <AnalyticsDashboard />
      )}

      {/* Quick Capture Floating Widget - Always visible */}
      <QuickCapture onCaptureCreated={() => setRefreshKey(k => k + 1)} />
    </Layout>
  );
};

export default Dashboard;
